import { useState } from "react";
import PropTypes from "prop-types";
import { isNil } from "ramda";

import FeaturePanelItemPresenter from "presenters/FeaturePanelItemPresenter";
import FeaturedPanelSlide from "components/FeaturedPanelSlide";
import withAlgoliaTracking from "components/withAlgoliaTracking";
import { CAROUSEL_DEFAULT_AUTOPLAY } from "enums/categories";
import { useTrackAccountEvents, useUsers } from "hooks";

import CategoryFeatureSkeleton from "./components/CategoryFeatureSkeleton";
import StyledSwiperSlide from "./components/Styled/StyledSwiperSlide";
import Slider from "./components/Slider";

const FeaturedPanelSlideWithAlgoliaTracking = withAlgoliaTracking(FeaturedPanelSlide);

const CategoryFeaturePanel = ({ items, loading, modulePosition, autoplayDelay = CAROUSEL_DEFAULT_AUTOPLAY }) => {
  const [slideDurations, setSlideDurations] = useState({});
  const { trackSelectItemEvents } = useTrackAccountEvents();
  const { currentUser } = useUsers();
  if (loading) return <CategoryFeatureSkeleton />;

  const filteredItems = items?.filter(
    (item) => !isNil(FeaturePanelItemPresenter.video(item)) || !isNil(FeaturePanelItemPresenter.image(item)),
  );

  const handleSetDuration = (index, duration) => {
    setSlideDurations((prevDurations) => ({
      ...prevDurations,
      [index]: duration,
    }));
  };

  const getAutoplayDelay = (index) => {
    let delayInSeconds = autoplayDelay;

    if (autoplayDelay === 0) {
      delayInSeconds = slideDurations[index] ? slideDurations[index] : CAROUSEL_DEFAULT_AUTOPLAY;
    }

    return delayInSeconds * 1000;
  };

  const onSlideClick = (index, item) => {
    trackSelectItemEvents(currentUser, item, modulePosition ? `${modulePosition}:${index + 1}` : undefined);
  };

  if (!filteredItems || filteredItems?.length === 0) return null;

  return (
    <Slider>
      {filteredItems?.map((item, index) => {
        return (
          <StyledSwiperSlide key={index} data-swiper-autoplay={getAutoplayDelay(index)}>
            <FeaturedPanelSlideWithAlgoliaTracking
              title={FeaturePanelItemPresenter.name(item)}
              description={FeaturePanelItemPresenter.description(item)}
              logo={FeaturePanelItemPresenter.logo(item)}
              video={FeaturePanelItemPresenter.video(item)}
              image={FeaturePanelItemPresenter.image(item)}
              backgroundColor={FeaturePanelItemPresenter.backgroundColor(item)}
              bottomBackgroundColor={FeaturePanelItemPresenter.bottomBackgroundColor(item)}
              link={FeaturePanelItemPresenter.link(item)}
              onVideoDuration={(duration) => handleSetDuration(index, duration)}
              item={FeaturePanelItemPresenter.normalize(item)}
              onClick={() => onSlideClick(index, FeaturePanelItemPresenter.normalize(item))}
            />
          </StyledSwiperSlide>
        );
      })}
    </Slider>
  );
};

CategoryFeaturePanel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      logo: PropTypes.string,
      video: PropTypes.string,
      image: PropTypes.string,
      backgroundColor: PropTypes.string,
      bottomBackgroundColor: PropTypes.string,
      link: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  autoplayDelay: PropTypes.number,
  modulePosition: PropTypes.number,
};

export default CategoryFeaturePanel;
